import type { Call } from "@frontierx/types";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

/**
 * Hook that monitors upcoming calls and invalidates queries when they should transition to "now"
 * This ensures calls don't disappear during the transition from "upcoming" to "now"
 */
export function useCallTransitionMonitor(
  calls: Call[],
  organizationId?: string,
) {
  const queryClient = useQueryClient();
  const timeoutsRef = useRef<Set<NodeJS.Timeout>>(new Set());

  useEffect(() => {
    // Clear existing timeouts
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current.clear();

    if (!organizationId || !calls.length) return;

    const now = new Date();

    // Find upcoming scheduled calls that will start soon
    const upcomingCalls = calls.filter(
      (call) =>
        call.status === "scheduled" &&
        new Date(call.startTime) > now &&
        new Date(call.startTime).getTime() - now.getTime() < 2 * 60 * 60 * 1000, // Within 2 hours
    );

    // Set timeouts to invalidate queries when each call should start
    for (const call of upcomingCalls) {
      const startTime = new Date(call.startTime);
      const timeUntilStart = startTime.getTime() - now.getTime();

      // Only set timeout if the call starts within a reasonable time frame
      if (timeUntilStart > 0 && timeUntilStart < 2 * 60 * 60 * 1000) {
        const timeout = setTimeout(() => {
          console.log(
            `Call ${call.id} should be starting now, invalidating queries`,
          );
          queryClient.invalidateQueries({
            queryKey: ["calls", organizationId],
          });
        }, timeUntilStart);

        timeoutsRef.current.add(timeout);
      }

      // Also set a timeout for 1 minute after start time to catch any delayed transitions
      const oneMinuteAfterStart = timeUntilStart + 60 * 1000;
      if (oneMinuteAfterStart > 0 && oneMinuteAfterStart < 2 * 60 * 60 * 1000) {
        const delayedTimeout = setTimeout(() => {
          console.log(
            `Delayed invalidation for call ${call.id} (1 minute after start)`,
          );
          queryClient.invalidateQueries({
            queryKey: ["calls", organizationId],
          });
        }, oneMinuteAfterStart);

        timeoutsRef.current.add(delayedTimeout);
      }
    }

    // Cleanup function
    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current.clear();
    };
  }, [calls, organizationId, queryClient]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current.clear();
    };
  }, []);
}
